import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookMarked, Tablet, X, Sparkles, ArrowRight } from "lucide-react";

/**
 * BookFloatingPromo
 * ─────────────────
 * Floating attention-grabbing promo for the new book ("2 tomi" hardcover
 * image). Sits on the RIGHT side of the viewport (desktop), or as a small
 * bottom-anchored card on mobile. Designed to be the first thing a doctor
 * sees when entering the app/blog, with a subtle but evident wobble +
 * pulsing gold glow on the cover image.
 *
 * UX rules:
 *  - Dismissible: once the user closes it, we store a flag in localStorage
 *    so we don't nag them on every page load.
 *  - Appears with a 1.2s delay so the dashboard greeting can register first.
 *  - Auto-reopens on next session (we use a 7-day TTL) so reminders return.
 *  - Designed for `position: fixed` — never blocks the bottom-right where
 *    chat widgets live (offset from the bottom right corner).
 */
const COVER =
  "https://customer-assets.emergentagent.com/job_italian-med-test/artifacts/y9xzaeeu_ChatGPT%20Image%2027%20may%202026%2C%2011_42_49%20a.m..png";

const PAPERBACK_URL = "https://amzn.eu/d/04XcFMQm";
const KINDLE_URL    = "https://amzn.eu/d/08hdRPPT";

const STORAGE_KEY = "mediquiz.book_promo_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function readDismissedAt() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export default function BookFloatingPromo() {
  // open is null on first render to avoid a flash before localStorage read.
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const dismissedAt = readDismissedAt();
    const stillSilenced = Date.now() - dismissedAt < DISMISS_TTL_MS;
    if (stillSilenced) {
      setOpen(false);
      return;
    }
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {
      /* ignore quota errors */
    }
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="book-floating-promo"
          initial={{ opacity: 0, x: 40, scale: 0.96 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 60, scale: 0.95, transition: { duration: 0.25 } }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="fixed z-40 right-4 bottom-4 sm:right-6 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 w-[88vw] max-w-[300px] sm:max-w-[290px]"
        >
          <div
            className="relative overflow-hidden rounded-2xl p-4 sm:p-5"
            style={{
              background:
                "linear-gradient(160deg, #0B0E14 0%, #161B25 50%, #0B0E14 100%)",
              border: "1px solid rgba(212, 175, 55, 0.45)",
              boxShadow:
                "0 24px 60px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(212,175,55,0.18) inset",
            }}
          >
            {/* Decorative ambient glow */}
            <div
              className="absolute inset-0 pointer-events-none opacity-50"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 20% 0%, rgba(212,175,55,0.18) 0%, transparent 50%), radial-gradient(circle at 80% 100%, rgba(201,49,74,0.18) 0%, transparent 50%)",
              }}
              aria-hidden="true"
            />

            {/* Close button */}
            <button
              type="button"
              onClick={dismiss}
              data-testid="book-floating-promo-close"
              aria-label="Chiudi promo libro"
              className="absolute top-2 right-2 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:bg-white/10"
              style={{ color: "rgba(244,239,230,0.6)" }}
            >
              <X className="h-3.5 w-3.5" />
            </button>

            {/* NEW eyebrow */}
            <div
              className="relative flex items-center gap-1.5 text-[10px] tracking-[0.3em] font-semibold mb-3"
              style={{ color: "#E9C779" }}
            >
              <Sparkles className="h-3 w-3" />
              NUOVO LIBRO DISPONIBILE
            </div>

            {/* Animated book cover */}
            <div className="relative flex justify-center mb-3" style={{ perspective: "900px" }}>
              <div
                className="absolute inset-0 pointer-events-none rounded-xl blur-2xl"
                style={{
                  background:
                    "radial-gradient(ellipse at center, rgba(212,175,55,0.35) 0%, transparent 65%)",
                  animation: "book-glow-pulse 3.2s ease-in-out infinite",
                }}
                aria-hidden="true"
              />
              <motion.img
                src={COVER}
                alt="Guida completa per medici in Italia · 2 tomi · Seconda edizione 2026"
                loading="lazy"
                data-testid="book-floating-promo-cover"
                className="relative w-[170px] sm:w-[200px] rounded-md shadow-[0_18px_36px_-12px_rgba(0,0,0,0.85)]"
                style={{ border: "1px solid rgba(212,175,55,0.35)" }}
                animate={{
                  rotate: [0, -1.2, 1, -0.6, 0],
                  y: [0, -2, 0, -1, 0],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  repeatDelay: 2.5,
                }}
              />
            </div>

            {/* Copy */}
            <h3
              className="relative font-serif text-base leading-tight mb-1.5"
              style={{ color: "#F4EFE6" }}
            >
              La <em className="italic" style={{ color: "#E9C779" }}>guida completa</em> per
              superare l&apos;esame di riconoscimento
            </h3>
            <p
              className="relative text-[12px] leading-snug mb-3"
              style={{ color: "rgba(244,239,230,0.7)" }}
            >
              2 tomi · Seconda edizione 2026. Disponibile in copertina morbida e Kindle.
            </p>

            {/* CTAs */}
            <div className="relative grid gap-2">
              <a
                href={PAPERBACK_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-floating-promo-paperback"
                className="group inline-flex items-center justify-between gap-2 rounded-lg px-3.5 py-2.5 text-sm font-semibold transition-all hover:scale-[1.02]"
                style={{
                  background:
                    "linear-gradient(90deg, #E9C779 0%, #F4DA9A 50%, #C99B3B 100%)",
                  color: "#1A0508",
                  boxShadow: "0 10px 22px -8px rgba(212,175,55,0.55)",
                }}
              >
                <span className="inline-flex items-center gap-2">
                  <BookMarked className="h-4 w-4" />
                  Copertina morbida
                </span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </a>
              <a
                href={KINDLE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-floating-promo-kindle"
                className="group inline-flex items-center justify-between gap-2 rounded-lg px-3.5 py-2.5 text-sm font-semibold transition-all hover:scale-[1.02]"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  color: "#F4EFE6",
                  border: "1px solid rgba(212,175,55,0.35)",
                }}
              >
                <span className="inline-flex items-center gap-2">
                  <Tablet className="h-4 w-4" style={{ color: "#E9C779" }} />
                  Kindle / eBook
                </span>
                <ArrowRight
                  className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                  style={{ color: "#E9C779" }}
                />
              </a>
            </div>

            <p
              className="relative text-[10px] text-center mt-3"
              style={{ color: "rgba(244,239,230,0.5)" }}
            >
              Acquisto su Amazon · spedizione e gestione Amazon.
            </p>
          </div>

          {/* Inline keyframes for the glow pulse animation. */}
          <style>{`
            @keyframes book-glow-pulse {
              0%, 100% { opacity: 0.45; transform: scale(0.92); }
              50%      { opacity: 0.85; transform: scale(1.05); }
            }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
